import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  createEmailServer,
  emailServersListQuery,
  emailServersShellQuery,
  removeEmailServer,
  saveConversationSettings,
  saveEmailServer,
  saveServerDispatching,
  type ConversationSettings,
  type EmailServerRow,
} from '../../api/settingsEaseedeskEmailServers'
import { toast } from '../../ui/notify'
import { LoadFailed } from '../../ui/LoadFailed'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Email servers.
 *
 * Element cu element fata de `version-3/easeedesk_settings/notifications/
 * servers/index.php` + `notificationServers.php`, si gest cu gest fata de
 * `index-1.js`: cele trei sectiuni (serverele, repartizarea pe tipuri de
 * tichet, serverul de conversatie), panourile care se deschid unul cate unul,
 * si casuta de repartizare care salveaza la schimbare, fara buton.
 *
 * PAROLA: casuta arata opt stelute cand exista una scrisa, si atat trimite
 * inapoi daca omul n-o atinge - ca in machete. Parola adevarata nu iese
 * niciodata din server.
 */
export function EmailServersPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(emailServersShellQuery())
  const list = useQuery(emailServersListQuery())
  const [open, setOpen] = useState(-1)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'email_servers'] })
  }

  const create = useMutation({
    mutationFn: () => createEmailServer(),
    onSuccess: reload,
    onError: () => toast('Something went wrong', 'Server settings', 'error'),
  })

  const remove = useMutation({
    mutationFn: (server: number) => removeEmailServer(server),
    onSuccess: reload,
    onError: () => toast('Something went wrong', 'Server settings', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed what="The email servers" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  const servers = list.data?.servers ?? []

  return (
    <div>
      <h4>
        <strong>Email servers</strong> settings
      </h4>
      <hr />

      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ marginBottom: 20 }}>Notification Email Servers</h5>
            <button
              type="button"
              className="btn btn-success btn-sm"
              id="new-notifications-server"
              disabled={create.isPending}
              onClick={() => create.mutate()}
            >
              <i className="fas fa-plus" aria-hidden="true"></i> Add server
            </button>
          </div>
          <div className="col-md-10 notification_servers_container">
            {list.error ? (
              <LoadFailed what="The servers" error={list.error} onRetry={() => void list.refetch()} />
            ) : !list.data ? (
              <Spinner />
            ) : servers.length === 0 ? (
              <div className="alert alert-info">No server configured.</div>
            ) : (
              <div className="panel-group" id="accordion" style={{ margin: 0 }} role="tablist">
                {servers.map((server, index) => (
                  <ServerPanel
                    key={server.ID}
                    server={server}
                    index={index}
                    open={open === index}
                    onToggle={() => setOpen(open === index ? -1 : index)}
                    onRemove={() => remove.mutate(server.ID)}
                    onSaved={reload}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <hr className="dotted" />
      </section>

      <section>
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ marginBottom: 20 }}>Ticket Notifications Dispatching</h5>
          </div>
          <div className="col-md-10">
            <table className="table table-hover">
              <thead style={{ backgroundColor: '#fff' }}>
                <tr>
                  <th>Ticket type</th>
                  <th width="50%"></th>
                  <th>Notification server</th>
                </tr>
              </thead>
              <tbody>
                {shell.data.ticket_type_servers.map((row) => (
                  <DispatchRow key={row.type_id} row={row} servers={shell.data.server_list} onSaved={reload} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <hr className="dotted" />
      </section>

      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ marginBottom: 20 }}>Conversation Email Server</h5>
          </div>
          <div className="col-md-10">
            <ConversationForm settings={shell.data.conversation} onSaved={reload} />
          </div>
        </div>
        <hr className="dotted" />
      </section>
    </div>
  )
}

/** Un rand din repartizare: casuta salveaza la schimbare, ca in `index-1.js`. */
function DispatchRow({
  row,
  servers,
  onSaved,
}: {
  row: { type_id: number; type_name: string; server_id: number }
  servers: { ID: number; scheme_name: string }[]
  onSaved(): void
}) {
  const [server, setServer] = useState(row.server_id)

  useEffect(() => setServer(row.server_id), [row.server_id])

  const save = useMutation({
    mutationFn: (next: number) => saveServerDispatching(row.type_id, next),
    onSuccess: () => {
      toast('Settings saved', 'Notifications')
      onSaved()
    },
    onError: () => toast('Something went wrong', 'Notifications', 'error'),
  })

  return (
    <tr>
      <td>{row.type_name}</td>
      <td style={{ textAlign: 'center' }}>
        <i className="fa fa-arrow-right" aria-hidden="true"></i>
      </td>
      <td>
        <select
          name="server_id"
          className="form-control input-sm servers-dropdown"
          value={server === 0 ? '' : String(server)}
          onChange={(e) => {
            const next = e.target.value === '' ? 0 : Number(e.target.value)
            setServer(next)
            save.mutate(next)
          }}
        >
          <option value="">Select...</option>
          {servers.map((option) => (
            <option key={option.ID} value={option.ID}>
              {option.scheme_name}
            </option>
          ))}
        </select>
      </td>
    </tr>
  )
}

/** Un panou de server, cu formularul lui. */
function ServerPanel({
  server,
  index,
  open,
  onToggle,
  onRemove,
  onSaved,
}: {
  server: EmailServerRow
  index: number
  open: boolean
  onToggle(): void
  onRemove(): void
  onSaved(): void
}) {
  const [form, setForm] = useState(() => serverFields(server))

  useEffect(() => setForm(serverFields(server)), [server])

  const save = useMutation({
    mutationFn: () => saveEmailServer(server.ID, form),
    onSuccess: () => {
      toast('Settings saved.', 'Server settings')
      onSaved()
    },
    onError: () => toast('Something went wrong', 'Server settings', 'error'),
  })

  const set = (key: string, value: string) => setForm((current) => ({ ...current, [key]: value }))

  return (
    <div className="panel panel-default">
      <div className="panel-heading" role="tab">
        <div className="panel-title font-14">
          <a
            style={{ padding: 0, display: 'inline-block' }}
            role="button"
            href={`#collapse-parsing-${index}`}
            aria-expanded={open}
            onClick={(e) => {
              e.preventDefault()
              onToggle()
            }}
          >
            <h5>
              <i className="far fa-envelope-open" aria-hidden="true"></i> #{index + 1} {server.scheme_name}
            </h5>
          </a>
          <button
            type="button"
            className="pull-right btn btn-sm btn-danger remove-server"
            style={{ marginTop: -4 }}
            onClick={onRemove}
          >
            Remove
          </button>
        </div>
      </div>
      {open && (
        <div id={`collapse-parsing-${index}`} className="panel-collapse collapse in" role="tabpanel">
          <div className="panel-body">
            <form
              className="notifications-settings-form"
              method="post"
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div className="form-group row">
                <Field label="Configuration name" name="scheme_name" value={form.scheme_name} onChange={set} />
              </div>
              <div className="form-group row">
                <Field label="Email address" name="email_address" type="email" value={form.email_address} onChange={set} />
                <Field label="From name" name="from_name" value={form.from_name} onChange={set} />
                <div className="col-md-4">
                  <input
                    className="icheck-servers-checkbox"
                    type="checkbox"
                    name="ticket_in_subject"
                    checked={form.ticket_in_subject === 'on'}
                    onChange={(e) => set('ticket_in_subject', e.target.checked ? 'on' : 'off')}
                  />
                  <label>Ticket ID in email subject</label>
                  <span className="help-block">(insert Ticket ID in subject ex: [##1234##])</span>
                </div>
              </div>

              <h5>SMTP Server</h5>
              <hr />
              <div className="form-group row">
                <div className="col-md-4">
                  <input
                    className="service-checkbox"
                    type="checkbox"
                    name="enable_smtp"
                    checked={form.enable_smtp === 'on'}
                    onChange={(e) => set('enable_smtp', e.target.checked ? 'on' : 'off')}
                  />
                  <label>Enable SMTP protocol</label>
                </div>
                <Field label="SMTP User" name="smtp_user" value={form.smtp_user} onChange={set} />
                <Field label="SMTP Password" name="smtp_password" type="password" value={form.smtp_password} onChange={set} />
                <Field label="SMTP Host" name="smtp_host" value={form.smtp_host} onChange={set} />
                <Field label="SMTP Port" name="smtp_port" value={form.smtp_port} onChange={set} />
                <Field label="SMTP Timeout (in seconds)" name="smtp_timeout" value={form.smtp_timeout} onChange={set} />
              </div>
              <div className="aright">
                <button className="btn btn-primary" type="submit" disabled={save.isPending}>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

/** Casuta de conversatie: acelasi formular, alt cap si alta tabela. */
function ConversationForm({ settings, onSaved }: { settings: ConversationSettings | null; onSaved(): void }) {
  const [form, setForm] = useState(() => conversationFields(settings))

  useEffect(() => setForm(conversationFields(settings)), [settings])

  const save = useMutation({
    mutationFn: () => saveConversationSettings(form),
    onSuccess: () => {
      toast('Settings updated.', 'Conversation')
      onSaved()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const set = (key: string, value: string) => setForm((current) => ({ ...current, [key]: value }))

  return (
    <form
      id="conversation-settings-form"
      method="post"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <div className="form-group row">
        <Field label="Email address" name="email_address" type="email" value={form.email_address} onChange={set} />
        <Field label="From name" name="from_name" value={form.from_name} onChange={set} />
        <div className="col-md-4">
          <input
            className="service-checkbox"
            type="checkbox"
            name="add_from_name"
            checked={form.add_from_name === 'on'}
            onChange={(e) => set('add_from_name', e.target.checked ? 'on' : 'off')}
          />
          <label>Use from name </label>
          <span className="help-block">(if not checked user name will be sent)</span>
        </div>
      </div>

      <h5>SMTP Server</h5>
      <hr />
      <div className="form-group row">
        <div className="col-md-4">
          <input
            className="service-checkbox"
            type="checkbox"
            name="enable_smtp"
            checked={form.enable_smtp === 'on'}
            onChange={(e) => set('enable_smtp', e.target.checked ? 'on' : 'off')}
          />
          <label>Enable SMTP protocol</label>
        </div>
        <Field label="SMTP User" name="smtp_user" value={form.smtp_user} onChange={set} />
        <Field label="SMTP Password" name="smtp_password" type="password" value={form.smtp_password} onChange={set} />
        <Field label="SMTP Host" name="smtp_host" value={form.smtp_host} onChange={set} />
        <Field label="SMTP Port" name="smtp_port" value={form.smtp_port} onChange={set} />
        <Field label="SMTP Timeout (in seconds)" name="smtp_timeout" value={form.smtp_timeout} onChange={set} />
      </div>
      <div className="aright">
        <button className="btn btn-primary" type="submit" disabled={save.isPending}>
          Save
        </button>
      </div>
    </form>
  )
}

function Field({
  label,
  name,
  value,
  type = 'text',
  onChange,
}: {
  label: string
  name: string
  value: string
  type?: string
  onChange(name: string, value: string): void
}) {
  return (
    <div className="col-md-4">
      <input
        className="form-control input-sm"
        placeholder={label}
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
      />
      <span className="help-block">{label}</span>
    </div>
  )
}

/** Randul serverului, ca perechi de text - asa cum pleaca la salvare. */
export function serverFields(server: EmailServerRow): Record<string, string> {
  return {
    scheme_name: server.scheme_name,
    email_address: server.email_address,
    from_name: server.from_name,
    enable_smtp: server.enable_smtp === 1 ? 'on' : 'off',
    smtp_user: server.smtp_user,
    smtp_password: server.has_password ? '********' : '',
    smtp_host: server.smtp_host,
    smtp_port: server.smtp_port,
    smtp_timeout: server.smtp_timeout,
    ticket_in_subject: server.ticket_in_subject === 1 ? 'on' : 'off',
  }
}

/** Setarile de conversatie, la fel. `null` = nu exista inca niciun rand. */
export function conversationFields(settings: ConversationSettings | null): Record<string, string> {
  return {
    email_address: settings?.email_address ?? '',
    from_name: settings?.from_name ?? '',
    add_from_name: settings?.add_from_name === 1 ? 'on' : 'off',
    enable_smtp: settings?.enable_smtp === 1 ? 'on' : 'off',
    smtp_user: settings?.smtp_user ?? '',
    smtp_password: settings?.has_password ? '********' : '',
    smtp_host: settings?.smtp_host ?? '',
    smtp_port: settings?.smtp_port ?? '',
    smtp_timeout: settings?.smtp_timeout ?? '',
  }
}
